/**
 * src/lib/auth/session.js
 * In-memory + sessionStorage token holder and an authenticated fetch wrapper
 * that transparently refreshes expired access tokens. Tokens are short-lived
 * signed JWTs issued by the server (Section 6).
 */
import { api } from '@/config';

const KEY_ACCESS = 'hemaai_access';
const KEY_REFRESH = 'hemaai_refresh';

export const getAccessToken = () => sessionStorage.getItem(KEY_ACCESS) || null;
export const getRefreshToken = () => sessionStorage.getItem(KEY_REFRESH) || null;

export function setSession({ accessToken, refreshToken }) {
  if (accessToken) sessionStorage.setItem(KEY_ACCESS, accessToken);
  if (refreshToken) sessionStorage.setItem(KEY_REFRESH, refreshToken);
}

export function clearSession() {
  sessionStorage.removeItem(KEY_ACCESS);
  sessionStorage.removeItem(KEY_REFRESH);
}

async function tryRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await fetch(api('auth-refresh'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    setSession(data);
    return true;
  } catch { return false; }
}

/** fetch() with the bearer token attached; retries once after a refresh on 401. */
export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : api(path);
  const withAuth = (token) => ({
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });

  let res = await fetch(url, withAuth(getAccessToken()));
  if (res.status === 401 && (await tryRefresh())) {
    res = await fetch(url, withAuth(getAccessToken()));
  }
  return res;
}
